import type { Diagnostic } from '@codemirror/lint';
import { linter, setDiagnostics } from '@codemirror/lint';
import type { Compartment } from '@codemirror/state';
import type { EditorView } from '@codemirror/view';
import type { RefObject } from 'react';

import { editorDiagnosticWrapperClass } from '../../../core/styles/diagnostic-tooltip.sty.js';
import { renderDiagnosticMessage } from '../../../core/utils/diagnostic-tooltip/render-diagnostic-message.js';
import type { InlineDiagnostic } from '../../code-editor/inline-diagnostic.js';

function mapInlineDiagnostic(d: InlineDiagnostic): Diagnostic {
  const severity = d.severity ?? 'error';

  return {
    from: d.from,
    to: d.to,
    message: d.message,
    severity,
    renderMessage: renderDiagnosticMessage(
      severity,
      d.message,
      editorDiagnosticWrapperClass,
    ),
  };
}

/**
 * Creates a CodeMirror linter extension that renders the provided inline diagnostics.
 * @param diagnosticsRef - A ref to the current inline diagnostics.
 * @returns a CodeMirror extension that renders the inline diagnostics.
 * @internal
 */
export function getDiagnosticsExtension(
  diagnosticsRef: RefObject<InlineDiagnostic[]>,
) {
  return linter(() => diagnosticsRef.current.map(mapInlineDiagnostic), {
    delay: 0,
  });
}

/**
 * Installs, updates, or removes the inline diagnostics extension.
 *
 * - `undefined → defined`: installs the linter via compartment reconfiguration.
 * - `defined → defined`: updates the ref and triggers a re-run without re-installing.
 * - `defined → undefined`: removes the linter via compartment reconfiguration.
 *
 * @param diagnostics - The inline diagnostics to render, or `undefined` to remove them.
 * @param editorViewRef - A ref to the editor view.
 * @param diagnosticsRef - A ref to the current inline diagnostics.
 * @param isEnabledRef - A ref to whether the linter is currently installed.
 * @param diagnosticsCompartment - The compartment used to manage the diagnostics extension.
 * @internal
 */
export function configureDiagnostics(
  diagnostics: InlineDiagnostic[] | undefined,
  editorViewRef: RefObject<EditorView>,
  diagnosticsRef: RefObject<InlineDiagnostic[]>,
  isEnabledRef: RefObject<boolean>,
  diagnosticsCompartment: Compartment,
): void {
  const editorView = editorViewRef.current;

  if (diagnostics !== undefined) {
    diagnosticsRef.current = diagnostics;

    // First time diagnostics are provided: install the linter extension.
    if (!isEnabledRef.current) {
      isEnabledRef.current = true;
      editorView.dispatch({
        effects: diagnosticsCompartment.reconfigure(
          getDiagnosticsExtension(diagnosticsRef),
        ),
      });
    } else {
      // Linter already installed: push the new diagnostics directly into the editor state.
      editorView.dispatch(
        setDiagnostics(
          editorView.state,
          diagnosticsRef.current.map(mapInlineDiagnostic),
        ),
      );
    }
  } else if (isEnabledRef.current) {
    // Diagnostics removed: uninstall the linter extension.
    diagnosticsRef.current = [];
    isEnabledRef.current = false;
    editorView.dispatch({
      effects: diagnosticsCompartment.reconfigure([]),
    });
  }
}
